/*
 * Anarvah Product Template v5 — interactivity for the custom product page.
 *
 * Self-contained, no jQuery, no build step. Looks for elements scoped under
 * `.anarvah-product-v5` and `.anarvah-product-v5-sticky` and wires up:
 *
 *  1. Pack selector  → Shopify variant_id sync. The "pack cards" are styled
 *     radio buttons mapped to product variants. Clicking a card updates the
 *     hidden variant_id input on the product form, the displayed prices, the
 *     ATC button availability state, and the sticky ATC bar's price.
 *  2. Qty selector   → + / − buttons next to the ATC button.
 *  3. Gallery thumbs → click thumbnail to swap the main hero image.
 *  4. Sticky ATC bar → fades in once the user scrolls past the main ATC.
 *     Tapping it scrolls back up and focuses the main ATC.
 *  5. Subscribe card → toggles a hidden "selling_plan" input when the user
 *     opts into Subscribe & Save. Only present when product has selling plans.
 */
(function () {
  'use strict';

  // ------------------------------------------------------------------
  // Utilities
  // ------------------------------------------------------------------
  function formatMoney(amountInPaise, format) {
    // Shopify gives us money in subunits (paise / cents). Default format is
    // Indian rupees with the symbol and grouping. Theme settings can override
    // the format string at render time — we keep this simple at the JS level.
    var asNumber = Number(amountInPaise) / 100;
    if (isNaN(asNumber)) return '';
    var formatted = asNumber.toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
    return '₹' + formatted;
  }

  function $(selector, root) { return (root || document).querySelector(selector); }
  function $$(selector, root) { return Array.prototype.slice.call((root || document).querySelectorAll(selector)); }

  // ------------------------------------------------------------------
  // Per-product hero wiring
  // ------------------------------------------------------------------
  function wireHero(hero) {
    var productJsonScript = hero.querySelector('script[type="application/json"][data-product-json]');
    if (!productJsonScript) return;
    var product;
    try { product = JSON.parse(productJsonScript.textContent); }
    catch (e) { console.warn('[anarvah-product-v5] product JSON failed to parse', e); return; }

    var packCards = $$('.pack-card', hero);
    var variantIdInput = $('input[name="id"]', hero);
    var qtyInput = $('.qty-num', hero);
    var qtyMinusBtn = $('.qty-btn[data-action="minus"]', hero);
    var qtyPlusBtn = $('.qty-btn[data-action="plus"]', hero);
    var atcBtn = $('.btn-atc', hero);
    var priceMain = $('.price-main', hero);
    var priceOrig = $('.price-orig', hero);
    var priceOff = $('.price-off', hero);
    var subscribeRow = $('.subscribe-row', hero);
    var sellingPlanInput = $('input[name="selling_plan"]', hero);

    // ---- Pack selector → variant sync ----
    function setSelectedVariant(variantId) {
      var variant = null;
      for (var i = 0; i < product.variants.length; i++) {
        if (String(product.variants[i].id) === String(variantId)) {
          variant = product.variants[i];
          break;
        }
      }
      if (!variant) return;

      // Update hidden variant_id input on form
      if (variantIdInput) variantIdInput.value = variant.id;

      // Update prices
      if (priceMain) priceMain.textContent = formatMoney(variant.price);
      if (priceOrig) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          priceOrig.textContent = formatMoney(variant.compare_at_price);
          priceOrig.style.display = '';
          if (priceOff) {
            var pct = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
            priceOff.textContent = pct + '% Off MRP';
            priceOff.style.display = '';
          }
        } else {
          priceOrig.style.display = 'none';
          if (priceOff) priceOff.style.display = 'none';
        }
      }

      // Active state on cards
      packCards.forEach(function (c) {
        c.classList.toggle('active', String(c.dataset.variantId) === String(variant.id));
      });

      // Availability
      if (atcBtn) {
        if (variant.available) {
          atcBtn.removeAttribute('disabled');
          atcBtn.textContent = atcBtn.dataset.labelAvailable || 'ADD TO CART';
        } else {
          atcBtn.setAttribute('disabled', 'disabled');
          atcBtn.textContent = atcBtn.dataset.labelSoldOut || 'SOLD OUT';
        }
      }

      // Sticky ATC bar price sync
      var stickyPrice = $('.anarvah-product-v5-sticky .sticky-price');
      if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);

      // Update the URL so refresh keeps the selected variant — but only
      // if we're already on a product detail page (no harm to the query).
      if (history && history.replaceState) {
        try {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          history.replaceState({}, '', url.toString());
        } catch (e) { /* ignore */ }
      }
    }

    packCards.forEach(function (card) {
      card.addEventListener('click', function () {
        if (card.dataset.unavailable === 'true') return;
        setSelectedVariant(card.dataset.variantId);
      });
    });

    // ---- Quantity buttons ----
    if (qtyInput && qtyMinusBtn && qtyPlusBtn) {
      qtyMinusBtn.addEventListener('click', function () {
        var v = parseInt(qtyInput.value, 10) || 1;
        qtyInput.value = Math.max(1, v - 1);
      });
      qtyPlusBtn.addEventListener('click', function () {
        var v = parseInt(qtyInput.value, 10) || 1;
        qtyInput.value = v + 1;
      });
    }

    // ---- Subscribe & Save toggle ----
    if (subscribeRow && sellingPlanInput) {
      subscribeRow.addEventListener('click', function (e) {
        // Don't toggle when the user is clicking inside the plan picker dropdown
        if (e.target.closest('.sub-plan-picker')) return;
        var active = subscribeRow.classList.toggle('active');
        if (active) {
          var firstPlan = subscribeRow.querySelector('[data-selling-plan-id]');
          sellingPlanInput.value = firstPlan ? firstPlan.dataset.sellingPlanId : '';
        } else {
          sellingPlanInput.value = '';
        }
      });
      var planRadios = $$('[data-selling-plan-radio]', subscribeRow);
      planRadios.forEach(function (radio) {
        radio.addEventListener('change', function () {
          if (subscribeRow.classList.contains('active')) {
            sellingPlanInput.value = radio.value;
          }
        });
      });
    }

    // ---- Gallery thumbnails ----
    // Clicking a thumb swaps the main hero image. The main <img> is
    // rendered with both `src` AND `srcset` (responsive sizes). Browsers
    // prefer srcset when both are set, so updating only `src` was being
    // silently ignored — the main image stayed on the featured media's
    // pre-rendered srcset URLs. Fix: update srcset FIRST (or remove it
    // if the thumb has none), then update src.
    //
    // Loading state: the swap may take time on slow connections. We
    // show a spinner overlay on .gallery-main BUT only after a 100ms
    // delay — instant (warm-cache) swaps fire the img `load` event
    // before the timeout, so the spinner never flashes. The 100ms
    // gate is what makes the loader feel "weightless" on fast loads
    // while still giving feedback on slow ones.
    var galleryThumbs = $$('.gallery-thumbs img', hero);
    var galleryMain = $('.gallery-main img', hero);
    var galleryWrap = $('.gallery-main', hero);
    var LOADER_DELAY_MS = 100;

    galleryThumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        galleryThumbs.forEach(function (t) { t.classList.remove('active'); });
        thumb.classList.add('active');
        if (!galleryMain) return;

        var newSrc = thumb.dataset.fullSrc || thumb.src;
        var newSrcset = thumb.dataset.fullSrcset || '';

        // No-op if the thumb is already the active image. Avoids
        // re-triggering the loader for redundant clicks.
        if (galleryMain.src === newSrc && galleryMain.srcset === newSrcset) return;

        // Schedule the loader appearance. If the new image loads
        // before this fires, we cancel it.
        var loaderTimer = setTimeout(function () {
          if (galleryWrap) galleryWrap.classList.add('is-loading');
        }, LOADER_DELAY_MS);

        function clearLoader() {
          clearTimeout(loaderTimer);
          if (galleryWrap) galleryWrap.classList.remove('is-loading');
          galleryMain.removeEventListener('load', clearLoader);
          galleryMain.removeEventListener('error', clearLoader);
        }
        galleryMain.addEventListener('load', clearLoader);
        galleryMain.addEventListener('error', clearLoader);

        // Swap srcset BEFORE src so the browser doesn't briefly pick
        // a stale variant.
        if (newSrcset) {
          galleryMain.srcset = newSrcset;
        } else {
          galleryMain.removeAttribute('srcset');
        }
        galleryMain.src = newSrc;

        // Hint the browser to decode off the main thread. `.decode()`
        // returns a Promise; we don't await it (the load handler is
        // the real signal that swap is done).
        if (galleryMain.decode) {
          galleryMain.decode().catch(function () { /* decode rejects
            on cancellation when another swap happens mid-load — safe to ignore */ });
        }
      });
    });

    // ---- Initial state: respect ?variant=… in URL ----
    var urlVariant = new URLSearchParams(window.location.search).get('variant');
    if (urlVariant) setSelectedVariant(urlVariant);
  }

  // ------------------------------------------------------------------
  // Cross-sell + Add button — AJAX add to cart + open drawer.
  //
  // The cross-sell card has two hit targets:
  //   <a class="gww-card-link">   image/name/price → PDP
  //   <button class="gww-add">    this button     → AJAX add
  // The button is a sibling of the link (not nested) so the click
  // never bubbles to the link. We still preventDefault as belt-and-
  // braces in case future markup changes re-nest them.
  //
  // The fetch payload mirrors what Dawn's product-form.js sends so
  // <cart-drawer> can re-render its sections in one round-trip:
  //   { items: [...], sections: "<id>,<id>,...", sections_url: "/pdp" }
  // On success we call cartDrawer.renderContents(response) — same
  // method product-form.js uses — which both updates the drawer's
  // markup AND opens it on the right.
  // ------------------------------------------------------------------
  function wireCrossSellAdd(hero) {
    var addBtn = $('[data-cross-sell-add]', hero);
    if (!addBtn) return;

    addBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();

      var variantId = addBtn.dataset.variantId;
      if (!variantId || addBtn.disabled || addBtn.dataset.loading === 'true') return;

      var defaultLabel = addBtn.dataset.defaultLabel || addBtn.textContent;

      addBtn.dataset.loading = 'true';
      addBtn.classList.add('is-loading');
      addBtn.textContent = '…';

      var cartDrawer = document.querySelector('cart-drawer') ||
                       document.querySelector('cart-notification');

      var sectionIds = [];
      if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        sectionIds = cartDrawer.getSectionsToRender().map(function (s) { return s.id; });
      }
      if (cartDrawer && typeof cartDrawer.setActiveElement === 'function') {
        cartDrawer.setActiveElement(document.activeElement);
      }

      var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
      var body = {
        items: [{ id: variantId, quantity: 1 }],
        sections: sectionIds.join(','),
        sections_url: window.location.pathname
      };

      function resetButton(label, delay) {
        setTimeout(function () {
          addBtn.textContent = label;
          addBtn.dataset.loading = 'false';
          addBtn.classList.remove('is-loading');
        }, delay || 0);
      }

      fetch(addUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(body)
      })
      .then(function (r) { return r.json(); })
      .then(function (response) {
        if (response.status && response.status !== 200) {
          // Shopify returns { status: 4xx, message, description } on error.
          // Fall back to a visible "Error" pip and log the description.
          console.warn('[cross-sell] add failed:', response.message || response.description);
          addBtn.textContent = 'Error';
          resetButton(defaultLabel, 1500);
          return;
        }
        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
          cartDrawer.renderContents(response);
          // CRITICAL: when the cart was empty before this add, the
          // <cart-drawer> element retains the `is-empty` class that
          // its server-rendered initial state sets. renderContents
          // removes is-empty from .drawer__inner but NOT from the
          // outer <cart-drawer>, and the CSS hides the items list
          // when the outer element still has is-empty — so the
          // drawer opens visually blank. Dawn's product-form.js
          // handles this in its .finally() block; we replicate here.
          if (cartDrawer.classList.contains('is-empty')) {
            cartDrawer.classList.remove('is-empty');
          }
        }
        addBtn.textContent = '✓ Added';
        resetButton(defaultLabel, 1500);
      })
      .catch(function (err) {
        console.error('[cross-sell] add error:', err);
        addBtn.textContent = 'Error';
        resetButton(defaultLabel, 1500);
      });
    });
  }

  // ------------------------------------------------------------------
  // Sticky ATC bar — visible only past the main ATC
  // ------------------------------------------------------------------
  function wireStickyBar() {
    var stickyBar = $('.anarvah-product-v5-sticky');
    var mainAtc = $('.anarvah-product-v5 .btn-atc');
    if (!stickyBar || !mainAtc) return;

    if (!('IntersectionObserver' in window)) {
      // Fallback for old browsers: always show after 600px scroll.
      window.addEventListener('scroll', function () {
        stickyBar.classList.toggle('show', window.scrollY > 600);
      });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        stickyBar.classList.toggle('show', !entry.isIntersecting);
      });
    }, { threshold: 0 });
    observer.observe(mainAtc);

    var stickyBtn = stickyBar.querySelector('.sticky-btn');
    if (stickyBtn) {
      stickyBtn.addEventListener('click', function (e) {
        e.preventDefault();
        var form = $('.anarvah-product-v5 form[action$="/cart/add"]');
        if (form) {
          // Submit the form — same Add to Cart path as the main button
          form.requestSubmit ? form.requestSubmit() : form.submit();
        }
      });
    }
  }

  // ------------------------------------------------------------------
  // Product detail tabs (Pass 2) — click a nav button, show that panel.
  // Self-contained per `[data-tabs-root]` so the section can render
  // standalone or alongside the hero without state collisions.
  // ------------------------------------------------------------------
  function wireTabs(root) {
    var buttons = $$('[data-tab-target]', root);
    var panels = $$('[data-tab-panel]', root);
    if (buttons.length === 0) return;

    function activate(targetId) {
      buttons.forEach(function (b) {
        var isActive = b.dataset.tabTarget === targetId;
        b.classList.toggle('active', isActive);
        b.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      panels.forEach(function (p) {
        p.classList.toggle('active', p.dataset.tabPanel === targetId);
      });
    }

    buttons.forEach(function (b) {
      b.addEventListener('click', function () { activate(b.dataset.tabTarget); });
      b.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        var idx = buttons.indexOf(b);
        var next = e.key === 'ArrowRight'
          ? buttons[(idx + 1) % buttons.length]
          : buttons[(idx - 1 + buttons.length) % buttons.length];
        next.focus();
        activate(next.dataset.tabTarget);
        e.preventDefault();
      });
    });
  }

  // ------------------------------------------------------------------
  // FAQ accordion (Pass 3) — click a question to toggle its answer.
  // ARIA-expanded + .open class toggled in tandem; the CSS handles
  // the visual reveal (display:none → block). Self-contained per
  // [data-faq-root] so multiple FAQ sections on one page don't fight.
  // ------------------------------------------------------------------
  function wireFaqs(root) {
    var toggles = $$('[data-faq-toggle]', root);
    toggles.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        btn.classList.toggle('open', !expanded);
        var answer = document.getElementById(btn.getAttribute('aria-controls'));
        if (answer) answer.classList.toggle('open', !expanded);
      });
    });
  }

  // ------------------------------------------------------------------
  // Init
  // ------------------------------------------------------------------
  function init() {
    var heroes = $$('.anarvah-product-v5');
    heroes.forEach(wireHero);
    heroes.forEach(wireCrossSellAdd);
    wireStickyBar();
    $$('[data-tabs-root]').forEach(wireTabs);
    $$('[data-faq-root]').forEach(wireFaqs);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
